import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Heart, Coffee, Calendar, Award, X, Star, MessageSquare, ChevronRight, Check, User, UserX } from 'lucide-react';
import { toast } from 'sonner';

const TIER_ICONS = {
  small: Coffee,
  medium: Calendar,
  large: Calendar,
  ambassador: Award
};

const StarRating = ({ value, onChange, label }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium text-slate-700">{label}</p>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star
              className={`h-7 w-7 transition-colors ${
                star <= (hover || value)
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-slate-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const DonationFeedbackModal = ({ open, onClose, onBypass }) => {
  const [view, setView] = useState('main'); // 'main', 'donate', 'ambassador-name', 'feedback', 'thanks'
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  
  // Ambassador name state
  const [ambassadorName, setAmbassadorName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  
  // Feedback state
  const [appRating, setAppRating] = useState(0);
  const [accuracyRating, setAccuracyRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  useEffect(() => {
    if (open) {
      setView('main');
      setAppRating(0);
      setAccuracyRating(0);
      setFeedbackText('');
      setAmbassadorName('');
      setIsAnonymous(false);
      setSelectedTier(null);
      loadTiers();
    }
  }, [open]);

  const loadTiers = async () => {
    try {
      const response = await api.getDonationTiers();
      setTiers(response.data.tiers);
    } catch (error) {
      console.error('Failed to load donation tiers:', error);
    }
  };

  const handleDonate = async (tierId) => {
    // If ambassador tier, show name prompt first
    if (tierId === 'ambassador') {
      setSelectedTier(tierId);
      setView('ambassador-name');
      return;
    }
    
    // For other tiers, proceed directly to checkout
    await proceedToCheckout(tierId, null);
  };

  const proceedToCheckout = async (tierId, displayName) => {
    setLoading(tierId);
    try {
      const originUrl = window.location.origin;
      const response = await api.createDonationCheckout(tierId, originUrl, displayName);
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      setLoading(null);
    }
  };

  const handleAmbassadorProceed = async () => {
    const displayName = isAnonymous ? null : (ambassadorName.trim() || null);
    await proceedToCheckout(selectedTier, displayName);
  };

  const handleSubmitFeedback = async () => {
    if (appRating === 0 && accuracyRating === 0) {
      toast.error('Please provide at least one rating');
      return;
    }
    
    setSubmittingFeedback(true);
    try {
      await api.submitQuickFeedback(appRating, accuracyRating, feedbackText || null);
      setView('thanks');
      toast.success('Thank you for your feedback!');
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleSkip = async () => {
    try {
      await api.bypassDonationPrompt();
      if (onBypass) onBypass();
    } catch (error) {
      console.error('Bypass error:', error);
    }
    onClose();
  };

  const handleClose = () => {
    if (view === 'thanks') {
      handleSkip();
    } else {
      onClose();
    }
  };

  // Main view - choice between donate or feedback
  if (view === 'main') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg" data-testid="donation-feedback-modal">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-100 to-sky-100 flex items-center justify-center">
                <Heart className="h-8 w-8 text-emerald-600" />
              </div>
            </div>
            <DialogTitle className="text-center text-2xl font-semibold">
              You're Using NoSpin-EBM!
            </DialogTitle>
            <DialogDescription className="text-center text-slate-600 mt-2">
              This tool is <strong>always free</strong>. There are two ways you can help make it better for everyone:
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 mt-4">
            {/* Donate Option */}
            <button
              onClick={() => setView('donate')}
              className="w-full p-4 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all flex items-center justify-between group"
              data-testid="choose-donate"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Heart className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900">Support with a Donation</p>
                  <p className="text-sm text-slate-500">Pay what you value, what you can afford</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-emerald-600" />
            </button>

            {/* Or Divider */}
            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-slate-200"></div>
              <span className="text-sm text-slate-400 font-medium">or</span>
              <div className="flex-1 h-px bg-slate-200"></div>
            </div>

            {/* Feedback Option */}
            <button
              onClick={() => setView('feedback')}
              className="w-full p-4 rounded-xl border border-slate-200 hover:border-sky-300 hover:bg-sky-50/50 transition-all flex items-center justify-between group"
              data-testid="choose-feedback"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-sky-100 flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-sky-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900">Contribute Feedback</p>
                  <p className="text-sm text-slate-500">Help us improve with your insights</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-sky-600" />
            </button>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-100">
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="w-full text-slate-500 hover:text-slate-700"
              data-testid="skip-all"
            >
              <X className="h-4 w-4 mr-2" />
              Not now, continue searching
            </Button>
            <p className="text-xs text-center text-slate-400 mt-2">
              You'll get 5 more searches before seeing this again
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Donate view
  if (view === 'donate') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg" data-testid="donation-modal">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-100 to-sky-100 flex items-center justify-center">
                <Heart className="h-8 w-8 text-emerald-600" />
              </div>
            </div>
            <DialogTitle className="text-center text-2xl font-semibold">
              Support Independent Research
            </DialogTitle>
            <DialogDescription className="text-center text-slate-600 mt-2">
              If you find value in NoSpin-EBM, consider a donation - but only what you can afford and feel it's worth. Every contribution helps keep AI-powered medical insights accessible to all.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 mt-4">
            {tiers.map((tier) => {
              const Icon = TIER_ICONS[tier.id] || Heart;
              return (
                <button
                  key={tier.id}
                  onClick={() => handleDonate(tier.id)}
                  disabled={loading !== null}
                  className="w-full p-4 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all flex items-center justify-between group"
                  data-testid={`donate-${tier.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                      <Icon className="h-5 w-5 text-slate-600 group-hover:text-emerald-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-slate-900">${tier.amount}</p>
                      <p className="text-sm text-slate-500">{tier.benefit}</p>
                    </div>
                  </div>
                  {loading === tier.id ? (
                    <span className="text-sm text-slate-500">Redirecting...</span>
                  ) : (
                    <span className="text-sm text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      Select →
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-6 pt-4 border-t border-slate-100 flex gap-2">
            <Button
              variant="outline"
              onClick={() => setView('main')}
              className="flex-1"
            >
              ← Back
            </Button>
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="flex-1 text-slate-500"
            >
              Skip for now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Ambassador name view - ask for name or anonymous
  if (view === 'ambassador-name') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg" data-testid="ambassador-name-modal">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                <Award className="h-8 w-8 text-amber-600" />
              </div>
            </div>
            <DialogTitle className="text-center text-2xl font-semibold">
              Become an Ambassador
            </DialogTitle>
            <DialogDescription className="text-center text-slate-600 mt-2">
              Your name will appear on our Ambassadors page to recognize your generous support. You can also choose to remain anonymous.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Name input option */}
            <button
              onClick={() => setIsAnonymous(false)}
              className={`w-full p-4 rounded-xl border transition-all flex items-center gap-3 ${
                !isAnonymous 
                  ? 'border-amber-300 bg-amber-50/50' 
                  : 'border-slate-200 hover:border-amber-200'
              }`}
              data-testid="choose-named"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                !isAnonymous ? 'bg-amber-100' : 'bg-slate-100'
              }`}>
                <User className={`h-5 w-5 ${!isAnonymous ? 'text-amber-600' : 'text-slate-500'}`} />
              </div>
              <div className="text-left">
                <p className="font-medium text-slate-900">Display my name</p>
                <p className="text-sm text-slate-500">Be recognized on the Ambassadors page</p>
              </div>
            </button>

            {/* Name input field - shown when not anonymous */}
            {!isAnonymous && (
              <div className="pl-4">
                <Input
                  value={ambassadorName}
                  onChange={(e) => setAmbassadorName(e.target.value)}
                  placeholder="Enter your name (e.g., Dr. Jane Smith)"
                  className="rounded-lg"
                  data-testid="ambassador-name-input"
                />
                <p className="text-xs text-slate-400 mt-1">
                  This name will be publicly displayed
                </p>
              </div>
            )}

            {/* Anonymous option */}
            <button
              onClick={() => { setIsAnonymous(true); setAmbassadorName(''); }}
              className={`w-full p-4 rounded-xl border transition-all flex items-center gap-3 ${
                isAnonymous 
                  ? 'border-slate-400 bg-slate-50' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid="choose-anonymous"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isAnonymous ? 'bg-slate-200' : 'bg-slate-100'
              }`}>
                <UserX className={`h-5 w-5 ${isAnonymous ? 'text-slate-700' : 'text-slate-500'}`} />
              </div>
              <div className="text-left">
                <p className="font-medium text-slate-900">Remain anonymous</p>
                <p className="text-sm text-slate-500">Support without public recognition</p>
              </div>
            </button>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-100 flex gap-2">
            <Button
              variant="outline"
              onClick={() => setView('donate')}
              className="flex-1"
            >
              ← Back
            </Button>
            <Button
              onClick={handleAmbassadorProceed}
              disabled={loading !== null || (!isAnonymous && !ambassadorName.trim())}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              data-testid="proceed-ambassador"
            >
              {loading ? 'Redirecting...' : 'Continue to Payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Feedback view
  if (view === 'feedback') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg" data-testid="feedback-modal">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sky-100 to-indigo-100 flex items-center justify-center">
                <MessageSquare className="h-8 w-8 text-sky-600" />
              </div>
            </div>
            <DialogTitle className="text-center text-2xl font-semibold">
              Help Us Improve
            </DialogTitle>
            <DialogDescription className="text-center text-slate-600 mt-2">
              Your feedback helps make NoSpin-EBM better for everyone. Even without donating, you're contributing to better healthcare AI.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            <StarRating
              value={appRating}
              onChange={setAppRating}
              label="How do you like the app experience?"
            />
            
            <StarRating
              value={accuracyRating}
              onChange={setAccuracyRating}
              label="How accurate are the search results?"
            />

            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">Additional comments (optional)</p>
              <Textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="What could we improve? Any suggestions?"
                className="resize-none"
                rows={3}
                data-testid="feedback-text"
              />
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-100 flex gap-2">
            <Button
              variant="outline"
              onClick={() => setView('main')}
              className="flex-1"
            >
              ← Back
            </Button>
            <Button
              onClick={handleSubmitFeedback}
              disabled={submittingFeedback || (appRating === 0 && accuracyRating === 0)}
              className="flex-1 bg-sky-600 hover:bg-sky-700"
              data-testid="submit-feedback"
            >
              {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Thanks view
  if (view === 'thanks') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md" data-testid="thanks-modal">
          <div className="text-center py-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">Thank You!</h2>
            <p className="text-slate-600 mb-6">
              Your feedback helps us build AI that clinicians can trust. Every insight makes NoSpin-EBM better for everyone.
            </p>
            <Button
              onClick={handleSkip}
              className="w-full bg-slate-900 hover:bg-slate-800"
              data-testid="continue-searching"
            >
              Continue Searching
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return null;
};

export default DonationFeedbackModal;
